import axios from 'axios';
import { fromUnixTime } from 'date-fns';
import _ from 'lodash';
import { getStepFunctionStatus, toggleAlertMessage } from '../action.js';
import { getOrganisation } from './organisationActions';
import { clearSurveyError } from './surveyActions';
import createSignedRequest from './createSignedRequest';
import createUnsignedRequest from './createUnsignedRequest.js';
const { apiURL, paymentURL } = require('./apiURL');

const Types = {
  STRIPE_INTENT: 'PAYMENT_STRIPE_INTENT',
  STRIPE_GETTING_COUPON: 'PAYMENT_STRIPE_GETTING_COUPON',
  STRIPE_COUPON: 'PAYMENT_STRIPE_COUPON',
  STRIPE_LIST_INVOICES: 'PAYMENT_STRIPE_LIST_INVOICES',
  STRIPE_UPDATE_SUBSCRIPTION: 'PAYMENT_STRIPE_UPDATE_SUBSCRIPTION',
  PAYMENT_SELECT_PRODUCT: 'PAYMENT_SELECT_PRODUCT',
  PAYMENT_SELECT_PRICE: 'PAYMENT_SELECT_PRICE',
  PAYMENT_SHOW_MODAl: 'PAYMENT_SHOW_MODAl',
  PAYMENT_SUBSCRIPTION_PLAN: 'PAYMENT_SUBSCRIPTION_PLAN',
  PAYMENT_BILLING_DETAILS: 'PAYMENT_BILLING_DETAILS',
  SET_STRIPE_CUSTOMER_ID: 'PAYMENT_SET_STRIPE_CUSTOMER_ID',
  LIST_STRIPE_PAYMENT_METHODS: 'PAYMENT_LIST_STRIPE_PAYMENT_METHODS',
  UPDATE_STRIPE_SUBSCRIPTION: 'PAYMENT_UPDATE_STRIPE_SUBSCRIPTION',
  UPDATE_PRICING_BANDS: 'PAYMENT_UPDATE_PRICING_BANDS',
  LIST_STRIPE_SUBSCRIPTIONS: 'PAYMENT_LIST_STRIPE_SUBSCRIPTIONS',
  LIST_STRIPE_PRICING_TIERS: 'PAYMENT_LIST_STRIPE_PRICING_TIERS',
  REGISTRATION_PLANS: 'PAYMENT_REGISTRATION_PLANS',
  REGISTRATION_PLANS_LOADING: 'PAYMENT_REGISTRATION_PLANS_LOADING',
  PLAN_UPGRADE_SELECTION: 'PAYMENT_PLAN_UPGRADE_SELECTION',
  ADDON_CATALOG: 'PAYMENT_ADDON_CATALOG',
  ADDON_CATALOG_LOADING: 'PAYMENT_ADDON_CATALOG_LOADING',
  ADDON_PURCHASE_LOADING: 'PAYMENT_ADDON_PURCHASE_LOADING',
  STRIPE_STATEMENT: 'PAYMENT_STRIPE_STATEMENT',
  STRIPE_STATEMENT_LOADING: 'PAYMENT_STRIPE_STATEMENT_LOADING',
  STRIPE_STATEMENT_PDF_LOADING: 'PAYMENT_STRIPE_STATEMENT_PDF_LOADING',
};

const buildStatementQuery = ({ from, to }) => {
  const params = new URLSearchParams({ from, to });
  return `?${params.toString()}`;
};

const setStatementLoading = (loading) => ({
  type: Types.STRIPE_STATEMENT_LOADING,
  loading,
});

const setStatementPdfLoading = (loading) => ({
  type: Types.STRIPE_STATEMENT_PDF_LOADING,
  loading,
});

const setStatement = (statement) => ({
  type: Types.STRIPE_STATEMENT,
  statement,
});

/**
 * Open a billing statement PDF from either Step Function or presigned URL response.
 * @param {object} dispatch
 * @param {object} data — axios response.data
 * @returns {boolean} whether a download was started
 */
const openStatementPdfResponse = (dispatch, data) => {
  const executionArn = data?.data?.executionArn;
  const url = data?.url;

  if (executionArn) {
    dispatch(getStepFunctionStatus(executionArn));
    return true;
  }

  if (url) {
    const popup = window.open(url, '_blank', 'noopener');
    if (!popup || popup.closed || typeof popup.closed === 'undefined') {
      dispatch(toggleAlertMessage(true, 'Popup blocker is enabled. Please allow popups for this site.', 'error'));
    }
    return true;
  }

  return false;
};

const selectProduct = (product) => ({
  type: Types.PAYMENT_SELECT_PRODUCT,
  product,
});

const selectPrice = (price) => ({
  type: Types.PAYMENT_SELECT_PRICE,
  price,
});

const subscriptionUpdated = (response) => ({
  type: Types.STRIPE_UPDATE_SUBSCRIPTION,
  payload: response,
});

export const updatingSubscription = (organisationRevenue) => {
  return async (dispatch, getState) => {
    const state = getState();

    console.log('updatingSubcription', organisationRevenue);

    const customerID = state.payment.stripe?.stripeCustomerID || '';

    try {
      const response = await axios(
        await createSignedRequest(
          'POST',
          paymentURL + '/payment/stripe/subscription',
          JSON.stringify({ organisationRevenue, customerID }),
          { Organisation: String(state.currentOrganisation) }
        )
      );

      console.log('updatingSubscription', response);
      dispatch(subscriptionUpdated(response.data.subscription));
    } catch (e) {
      console.log('updatingSubscription Error:', e);
    }
  };
};

const showSubscriptionModal = (status) => ({
  type: Types.PAYMENT_SHOW_MODAl,
  status,
});

const subscriptionPlan = (subscriptionPlan) => ({
  type: Types.PAYMENT_SUBSCRIPTION_PLAN,
  subscriptionPlan,
});

const registrationBillingDetails = (billingDetails) => ({
  type: Types.PAYMENT_BILLING_DETAILS,
  billingDetails,
});

const gettingCoupon = (status) => ({
  type: Types.STRIPE_GETTING_COUPON,
  status,
});

const stripeCoupon = (stripeCoupon, code = null) => ({
  type: Types.STRIPE_COUPON,
  stripeCoupon,
  code,
});

const getStripeCoupon = (coupon) => {
  return async (dispatch, getState) => {
    const state = getState();
    const { stripeAccountId } = state.whiteLabelAdmin || null;

    dispatch(gettingCoupon(true));

    try {
      const response = await fetch(
        apiURL + `/registration/stripe/promoCode?${stripeAccountId ? `stripeAccountId=${stripeAccountId}` : ``}`,
        {
          method: 'POST',
          body: JSON.stringify({ coupon }),
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        }
      );
      const data = await response.json();
      dispatch(stripeCoupon(data, coupon));
      dispatch(gettingCoupon(false));
      return data;
    } catch (error) {
      console.log('Error Fetching Stripe Coupon', error);
      dispatch(gettingCoupon(false));
      return null;
    }
  };
};

const stripeIntent = (response) => ({
  type: Types.STRIPE_INTENT,
  payload: response,
});

/**
 * Create Stripe Checkout Session.
 * Legacy registration passes paymentMethod only.
 * SME upgrade / trial convert passes planId, fteBand, fte, convertTrial, billingInterval, promoCode.
 * Any applied promotion code is sent as promoCode/coupon on both paths.
 *
 * @param {string} paymentMethod — 'card' | 'bacs_debit'
 * @param {object} [options]
 * @param {string} [options.planId]
 * @param {string} [options.fteBand]
 * @param {number|null} [options.fte]
 * @param {boolean} [options.convertTrial]
 * @param {'annual'|'monthly'} [options.billingInterval]
 * @param {string} [options.promoCode]
 * @returns {Function} thunk
 */
const createStripeCheckoutSession = (paymentMethod, options = {}) => {
  return async (dispatch, getState) => {
    const state = getState();

    const { organisation: { industry, currency } = {} } = state.profile?.registrationDetails || {};
    const billingDetails = state.payment?.billingDetails;
    const planUpgrade = state.payment?.planUpgrade || {};

    const planId = options.planId || planUpgrade.planId;
    const fteBand = options.fteBand || planUpgrade.fteBand;
    const fte = options.fte ?? planUpgrade.fte ?? null;
    const convertTrial =
      options.convertTrial === true ||
      planUpgrade.convertTrial === true ||
      state.organisation?.details?.subscriptionStatus === 'trialing' ||
      state.organisation?.details?.isTrial === true;
    const billingInterval = options.billingInterval || planUpgrade.billingInterval || 'annual';
    // The backend resolves either the customer-facing code or a promotion code id (promo_…).
    const promoCode =
      options.promoCode ||
      planUpgrade.promoCode ||
      state.payment?.registrationPlans?.promo?.code ||
      state.payment?.stripeCoupon?.code ||
      state.payment?.registrationPlans?.promo?.id ||
      state.payment?.stripeCoupon?.id ||
      state.profile?.registrationDetails?.organisation?.stripeCoupon ||
      null;

    localStorage.setItem('Organisation', state.currentOrganisation);

    const body = {
      paymentMethod,
      billingDetails: { ...billingDetails, industry, currency: currency || billingDetails?.currency || 'GBP' },
    };

    if (planId) {
      body.planId = planId;
      body.fteBand = fteBand || null;
      body.fte = fte;
      body.billingInterval = billingInterval === 'monthly' ? 'monthly' : 'annual';
      body.convertTrial = !!convertTrial;
    }

    if (promoCode) {
      body.promoCode = promoCode;
      body.coupon = promoCode;
    }

    try {
      const response = await axios(
        await createSignedRequest(
          'POST',
          paymentURL + '/payment/stripe/checkout-session',
          JSON.stringify(body),
          { Organisation: String(state.currentOrganisation) }
        )
      );

      const data = response.data || {};
      if (data.error) {
        const err = new Error(data.message || data.error);
        err.code = data.error;
        dispatch(toggleAlertMessage(true, data.message || data.error, 'error'));
        throw err;
      }

      console.log('createStripeCheckoutSession', response);
      dispatch(stripeIntent(data));
      return data;
    } catch (e) {
      console.log('createStripeCheckoutSession Error:', e);
      const apiError = e?.response?.data;
      if (apiError?.error) {
        e.code = apiError.error;
        const message =
          apiError.error === 'plan_not_self_serve'
            ? 'This plan is not currently available for self-serve Checkout. Please contact sales.'
            : apiError.message || apiError.error;
        dispatch(toggleAlertMessage(true, message, 'error'));
      } else if (!e.code) {
        dispatch(toggleAlertMessage(true, e.message, 'error'));
      }
      throw e;
    }
  };
};

const createStripeCheckoutSessionSetupBacs = (stripeCustomerId) => {
  return async (dispatch, getState) => {
    const state = getState();
    try {
      const response = await axios(
        await createSignedRequest(
          'POST',
          paymentURL + '/payment/stripe/checkout-session/setup/bacs_debit',
          JSON.stringify( { stripeCustomerId, Organisation: String(state.currentOrganisation) } ),
        )
      );
      console.log('createStripeCheckoutSessionSetupBacs', response);
      const checkoutUrl = response.data.checkoutUrl;
      if(checkoutUrl)
        window.location.replace(checkoutUrl);
    } catch (e) {
      console.log('createStripeCheckoutSessionSetupBacs Error:', e);
      toggleAlertMessage(true, e.message, 'error');
    }
  };
};

const attachPaymentMethodToSubscription = ( sessionId ) => {
  return async (dispatch, getState) => {
    const state = getState();
    const { stripeSubscriptions } = state?.payment?.stripe || {};
    let lastSubscription;
    if (Array.isArray(stripeSubscriptions) && stripeSubscriptions.length >= 1) {
      lastSubscription = _.orderBy(stripeSubscriptions, (sub) => fromUnixTime(sub.created), 'desc')?.[0];
    }
    if (!lastSubscription) {
      window.alert('No subscription found');
      return;
    }
    try {
      const response = await axios(
        await createSignedRequest(
          'POST',
          paymentURL + '/payment/stripe/attach-payment-method',
          JSON.stringify( { 
            sessionId,
            subscriptionId: lastSubscription?.id,
            Organisation: String(state.currentOrganisation) 
          } ),
        )
      );

      console.log('attachPaymentMethodToSubscription', response);
    } catch (e) {
      console.log('attachPaymentMethodToSubscription Error:', e);
      toggleAlertMessage(true, e.message, 'error');
    }
  };
};

const getStripeIntent = () => {
  return async (dispatch, getState) => {
    const state = getState();
    const stripeCustomerId = state.payment.stripe?.stripeCustomerId || '';

    if (state.currentOrganisation) {
      try {
        const response = await axios(
          await createSignedRequest('GET', paymentURL + `/payment/stripe/intent?customerId=${stripeCustomerId}`, null, {
            Organisation: String(state.currentOrganisation),
          })
        );

        console.log('getStripeIntent', response);

        const { client_secret } = response.data || {};
        dispatch(stripeIntent({ client_secret }));
      } catch (error) {
        console.log('getStripeIntent Error:', error);
      }
    }
  };
};

const listStripeInvoices = (invoices) => ({
  type: Types.STRIPE_LIST_INVOICES,
  payload: { invoices },
});

const getStripeInvoices = () => {
  return async (dispatch, getState) => {
    const state = getState();
    const orgId = state.currentOrganisation;

    dispatch(listStripeInvoices({ invoices: [], loader: true }));

    try {
      const response = await axios(
        await createSignedRequest('GET', paymentURL + '/payment/stripe/billing', null, { Organisation: String(orgId) })
      );

      console.log('getStripeInvoices', response);

      const { error, invoices } = response.data || {};

      if (error) {
        dispatch(listStripeInvoices({ invoices: [], loader: false }));
        return dispatch(toggleAlertMessage(true, error, 'error'));
      }

      dispatch(listStripeInvoices({ invoices, loader: false }));
    } catch (e) {
      dispatch(toggleAlertMessage(true, 'There was problem while fetching your invoices.', 'error'));
      dispatch(listStripeInvoices({ invoices: [], loader: false }));
      console.log('getStripeInvoices Error:', e);
    }
  };
};

const sendUserOrderForm = (orgId) => {
  return async (dispatch, getState) => {
    try {
      const response = await axios(
        await createSignedRequest('POST', paymentURL + '/payment/stripe/orderForm', '{}', { Organisation: String(orgId) })
      );

      console.log('sendUserOrderForm', response);
    } catch (e) {
      console.log('sendUserOrderForm Error:', e);
    }
  };
};

const updatePricingBands = (pricingBands) => ({
  type: Types.UPDATE_PRICING_BANDS,
  pricingBands,
});

const savePricingBand = (band) => {
  return async (dispatch, getState) => {
    const state = getState();

    try {
      const response = await axios(
        await createSignedRequest('POST', apiURL + '/admin/pricingBands', JSON.stringify({ band }), {
          Organisation: String(state.currentOrganisation),
        })
      );

      console.log('savePricingBand', response);

      const { band: saveBand, error } = response.data || {};

      if (error) dispatch(toggleAlertMessage(true, 'User not authorised', 'error'));
      else if (saveBand) dispatch(updatePricingBands(saveBand));

      console.log('savePricingBand', response);
    } catch (e) {
      console.log('savePricingBand Error:', e);
    }
  };
};

const setStripeCustomerId = (response) => ({
  type: Types.SET_STRIPE_CUSTOMER_ID,
  response,
});

const getStripeCustomerId = () => {
  return async (dispatch, getState) => {
    const state = getState();

    try {
      const response = await axios(
        await createSignedRequest('GET', paymentURL + `/payment/stripe/stripeCustomerId`, null, {
          Organisation: String(state.currentOrganisation),
        })
      );

      const { stripeCustomerId } = response.data || {};

      dispatch(setStripeCustomerId(stripeCustomerId));

      return stripeCustomerId;
    } catch (err) {
      console.log('Error Getting Stripe Customer', err.message);
    }
  };
};

const getStripeCustomerIdForDisplay = (orgId) => {
  return async (dispatch, getState) => {
    try {
      const response = await axios(
        await createSignedRequest('GET', paymentURL + `/payment/stripe/stripeCustomerId`, null, {
          Organisation: String(orgId),
        })
      );

      const { stripeCustomerId } = response.data || {};
      return stripeCustomerId;
    } catch (err) {
      console.log('Error Getting Stripe Customer', err.message);
    }
  };
};

const listPaymentMethods = (paymentMethods, loader) => ({
  type: Types.LIST_STRIPE_PAYMENT_METHODS,
  paymentMethods,
  loader,
});

const getStripePaymentMethods = () => {
  return async (dispatch, getState) => {
    const state = getState();

    const stripeCustomerId = state.payment?.stripe?.stripeCustomerId || '';

    dispatch(listPaymentMethods({}, true));
    try {
      const response = await axios(
        await createSignedRequest('GET', paymentURL + `/payment/stripe/paymentMethods?customerId=${stripeCustomerId}`, '', {
          Organisation: String(state.currentOrganisation),
        })
      );

      const { error, paymentMethods, defaultPaymentMethod, subscriptions } = response.data || {};
      if (error) dispatch(toggleAlertMessage(true, error, 'error'));
      else dispatch(listPaymentMethods({ paymentMethods, defaultPaymentMethod, subscriptions }, false));
    } catch (err) {
      dispatch(listPaymentMethods([], false));
    }
  };
};

export const listSubscriptions = (subscriptions) => ({
  type: Types.LIST_STRIPE_SUBSCRIPTIONS,
  subscriptions,
});

const getSubscription = () => {
  return async (dispatch, getState) => {
    const state = getState();
    const stripeCustomerId = state.payment?.stripe?.stripeCustomerId || '';

    try {
      const response = await axios(
        await createSignedRequest('GET', paymentURL + `/payment/stripe/subscriptions?customerId=${stripeCustomerId}`, '', {
          Organisation: String(state.currentOrganisation),
        })
      );

      console.log('getSubscription', response);

      dispatch(listSubscriptions(response.data.subscriptions));

      return response;
    } catch (err) {}
  };
};

const createStripeCustomer = (createIntent) => {
  return async (dispatch, getState) => {
    const state = getState();
    const { firstName, lastName, email } = state.profile?.details || {};
    const { vatNumber } = state.organisation?.details || {};
    const customerDetails = { name: firstName + ' ' + lastName, email, vatNumber };

    try {
      const response = await axios(
        await createSignedRequest(
          'POST',
          paymentURL + `/payment/stripe/createStripeCustomer`,
          JSON.stringify({ createIntent, customerDetails }),
          {
            Organisation: String(state.currentOrganisation),
          }
        )
      );

      const { stripeCustomerId, client_secret } = response.data || {};
      if (client_secret) dispatch(stripeIntent({ client_secret }));
      dispatch(setStripeCustomerId(stripeCustomerId));
      return response;
    } catch (err) {
      dispatch(setStripeCustomerId({ error: 'Error Creating Stripe Customer' }));
    }
  };
};

const updateSubscription = (response) => ({
  type: Types.UPDATE_STRIPE_SUBSCRIPTION,
  response,
});

const listStripePricingTiers = (response) => ({
  type: Types.LIST_STRIPE_PRICING_TIERS,
  response,
});

const updateStripeSubscriptions = (subId, field, value) => {
  // Field is the property to update on the stripe subscripton object  and value, the value to be updated
  return async (dispatch, getState) => {
    const state = getState();

    const stripeCustomerId = state.payment?.stripe?.stripeCustomerId || '';
    // subId is an array of subscription ids
    const subscriptionId = subId.length === 1 ? subId[0] : subId;

    if (stripeCustomerId) {
      const response = await axios(
        await createSignedRequest(
          'POST',
          paymentURL + `/payment/stripe/updateSubscription?customerId=${stripeCustomerId}&field=${field}&value=${value}`,
          JSON.stringify({ subscriptionId }),
          {
            Organisation: String(state.currentOrganisation),
          }
        )
      );
      console.log('updateStripeSubscriptions', response);
      if (field.includes('payment')) {
        // The update was regarding payment method, so we need to update the payment methods
        dispatch(getStripePaymentMethods());
      }
      dispatch(updateSubscription(response.data));
    } else {
      dispatch(toggleAlertMessage(true, 'Stripe Customer Id not found', 'error'));
    }
  };
};

const updateStripeCustomer = (field, value) => {
  // Field is the property to update on the stripe subscripton object and value, the value to be updated
  return async (dispatch, getState) => {
    const state = getState();

    const stripeCustomerId = state.payment?.stripe?.stripeCustomerId || '';

    if (stripeCustomerId) {
      const body = { stripeCustomerId, field, value };
      const response = await axios(
        await createSignedRequest('POST', paymentURL + `/payment/stripe/updateStripeCustomer`, JSON.stringify(body), {
          Organisation: String(state.currentOrganisation),
        })
      );
      console.log('updateStripeCustomer', response);

      if (field.includes('payment')) {
        // The update was regarding payment method, so we need to update the payment methods
        dispatch(getStripePaymentMethods());
      }
    } else {
      dispatch(toggleAlertMessage(true, 'Stripe Customer Id not found', 'error'));
    }
  };
};

const removePaymentMethod = (id) => {
  return async (dispatch, getState) => {
    const state = getState();
    const stripeCustomerId = state.payment?.stripe?.stripeCustomerId || '';

    if (stripeCustomerId) {
      const response = await axios(
        await createSignedRequest('DELETE', paymentURL + '/payment/stripe/removePaymentMethod', JSON.stringify({ id }), {
          Organisation: String(state.currentOrganisation),
        })
      );
      console.log('removePaymentMethod', response);
      const { success, message, error } = response.data || {};
      if (success) {
        dispatch(toggleAlertMessage(true, message, 'success'));
        dispatch(getStripePaymentMethods());
      } else {
        console.log('removePaymentMethod Error', error);
        dispatch(toggleAlertMessage(true, message, 'error'));
      }
    } else {
      dispatch(toggleAlertMessage(true, 'Stripe Customer Id not found', 'error'));
    }
  };
};

const getSubscriptionPrice = (priceId, quantity) => {
  return async (dispatch, getState) => {
    const state = getState();

    const response = await axios(
      await createSignedRequest(
        'GET',
        paymentURL + `/payment/stripe/subscriptionPrice?priceId=${priceId}&quantity=${quantity}`,
        null,
        {
          Organisation: String(state.currentOrganisation),
        }
      )
    );
    console.log('getSubscriptionPrice', response);

    const { success, message, error } = response.data || {};
    if (success) {
      return response.data;
    } else {
      const errMsg = message ? message : error;
      console.log('getSubscriptionPrice Error', error);
      dispatch(toggleAlertMessage(true, errMsg, 'error'));
    }
  };
};

const getStripePricingTiers = () => {
  return async (dispatch) => {
    const response = await createUnsignedRequest('GET', apiURL + `/registration/stripePricingTiers`, null, {})
      .then((response) => {
        if (response.ok) return response.json();
      })
      .then((data) => dispatch(listStripePricingTiers(data)));

    return response;
  };
};

const setRegistrationPlansLoading = (loading) => ({
  type: Types.REGISTRATION_PLANS_LOADING,
  loading,
});

const setRegistrationPlans = (payload) => ({
  type: Types.REGISTRATION_PLANS,
  payload,
});

/**
 * CYF-direct SME catalog: GET /registration/plans, optionally resolved with a promotion code.
 * Returns 404 `{ error: 'plans_not_available' }` for white-label origins.
 * @param {string|null} [promoCode]
 * @returns {Function} thunk resolving to `{ currency, plans, fteBands, promo }` or null
 */
const getRegistrationPlans = (promoCode = null) => {
  return async (dispatch) => {
    dispatch(setRegistrationPlansLoading(true));
    try {
      const promoQuery = promoCode?.trim() ? `?promoCode=${encodeURIComponent(promoCode.trim())}` : '';
      const response = await createUnsignedRequest('GET', apiURL + `/registration/plans${promoQuery}`, null, {});
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const err = new Error(data.message || data.error || 'Failed to load plans');
        err.code = data.error || (response.status === 404 ? 'plans_not_available' : 'plans_error');
        err.status = response.status;
        dispatch(setRegistrationPlans({ currency: 'GBP', plans: [], fteBands: [], promo: null, error: err.code }));
        throw err;
      }

      dispatch(
        setRegistrationPlans({
          currency: data.currency || 'GBP',
          plans: data.plans || [],
          fteBands: data.fteBands || [],
          promo: data.promo || null,
          error: null,
        })
      );
      return data;
    } catch (e) {
      console.log('getRegistrationPlans Error:', e);
      if (!e.code) {
        dispatch(setRegistrationPlans({ currency: 'GBP', plans: [], fteBands: [], promo: null, error: 'plans_error' }));
      }
      throw e;
    } finally {
      dispatch(setRegistrationPlansLoading(false));
    }
  };
};

/**
 * Persist selected SME plan upgrade options for checkout-session.
 * @param {object|null} selection
 */
const setPlanUpgradeSelection = (selection) => ({
  type: Types.PLAN_UPGRADE_SELECTION,
  selection,
});

/**
 * In-app sales handoff for Discover/Govern (or any plan).
 * POST /registration/contactSales → Customer.io sales_contact_requested.
 *
 * @param {object} payload
 * @param {string} [payload.planId]
 * @param {string} [payload.fteBand]
 * @param {number|null} [payload.fte]
 * @param {string} [payload.message]
 * @param {string} [payload.journey]
 * @returns {Function} thunk
 */
const contactSales = (payload = {}) => {
  return async (dispatch, getState) => {
    const state = getState();
    const profile = state.profile?.details || {};
    const org = state.organisation?.details || {};

    const body = {
      source: 'in_app_plan',
      user: {
        email: profile.email,
        firstName: profile.firstName,
        lastName: profile.lastName,
      },
      organisation: {
        name: org.name,
      },
      planId: payload.planId || null,
      fteBand: payload.fteBand || org.fteBand || null,
      fte: payload.fte ?? org.fte ?? null,
      journey: payload.journey || null,
      message: payload.message || null,
    };

    try {
      const response = await fetch(apiURL + '/registration/contactSales', {
        method: 'POST',
        body: JSON.stringify(body),
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.error) {
        const msg = data.message || data.error || 'Could not notify sales.';
        dispatch(toggleAlertMessage(true, msg, 'error'));
        const err = new Error(msg);
        err.code = data.error;
        throw err;
      }

      dispatch(toggleAlertMessage(true, data.message || 'Sales team has been notified.', 'success'));
      return data;
    } catch (e) {
      console.log('contactSales Error:', e);
      if (!e.code) {
        dispatch(toggleAlertMessage(true, e.message || 'Could not notify sales.', 'error'));
      }
      throw e;
    }
  };
};

const setAddonCatalog = (catalog) => ({
  type: Types.ADDON_CATALOG,
  catalog,
});

const setAddonCatalogLoading = (loading) => ({
  type: Types.ADDON_CATALOG_LOADING,
  loading,
});

const setAddonPurchaseLoading = (loading) => ({
  type: Types.ADDON_PURCHASE_LOADING,
  loading,
});

/**
 * Fetch the add-on product catalog for this org.
 * Response: `{ products: [...] }` where each product has `addon_ids`, `feature_keys`, pricing, and `entitled`.
 * @returns {Function} thunk
 */
const getAddonCatalog = () => {
  return async (dispatch, getState) => {
    const state = getState();
    dispatch(setAddonCatalogLoading(true));
    try {
      const response = await axios(
        await createSignedRequest('GET', apiURL + '/my/addOns/catalog', null, {
          Organisation: String(state.currentOrganisation),
        }),
      );
      const { products } = response.data || {};
      dispatch(setAddonCatalog(products || []));
    } catch (e) {
      console.log('getAddonCatalog Error:', e);
      dispatch(toggleAlertMessage(true, 'Failed to load add-on catalog', 'error'));
    } finally {
      dispatch(setAddonCatalogLoading(false));
    }
  };
};

/**
 * Purchase an add-on product (adds to Stripe subscription). One product may grant multiple add-on UUIDs.
 * @param {string} productId - The add-on product UUID from the catalog
 * @param {string} billingType - "annual" or "monthly"
 * @returns {Function} thunk
 */
const purchaseAddOn = (productId, billingType) => {
  return async (dispatch, getState) => {
    const state = getState();
    dispatch(setAddonPurchaseLoading(true));
    try {
      const response = await axios(
        await createSignedRequest(
          'POST',
          paymentURL + '/payment/addOn/purchase',
          JSON.stringify({ productId, billingType }),
          { Organisation: String(state.currentOrganisation) },
        ),
      );
      const { success, error } = response.data || {};
      if (success) {
        dispatch(toggleAlertMessage(true, 'Add-on purchased successfully', 'success'));
        await dispatch(getOrganisation());
        await dispatch(getAddonCatalog());
        dispatch(clearSurveyError());
        return response.data;
      } else {
        dispatch(toggleAlertMessage(true, error || 'Purchase failed', 'error'));
        return null;
      }
    } catch (e) {
      console.log('purchaseAddOn Error:', e);
      const { status, data = {} } = e?.response || {};
      if (status === 409) {
        await dispatch(getOrganisation());
        await dispatch(getAddonCatalog());
        dispatch(clearSurveyError());
        dispatch(toggleAlertMessage(true, 'This add-on is already active.', 'success'));
        return { success: true, alreadyEntitled: true };
      }
      const noPaidSubscription = [
        'Organisation has no Stripe customer',
        'No active subscription found',
      ].includes(data.error);
      dispatch(
        toggleAlertMessage(
          true,
          noPaidSubscription
            ? 'Add-ons require an active paid subscription. Choose a plan before purchasing this add-on.'
            : data.error || 'Failed to purchase add-on',
          'error',
        ),
      );
      return null;
    } finally {
      dispatch(setAddonPurchaseLoading(false));
    }
  };
};

/**
 * Cancel an add-on product subscription (end-of-period cancellation, min-term enforced).
 * @param {string} productId - The add-on product UUID
 * @returns {Function} thunk
 */
const cancelAddOn = (productId) => {
  return async (dispatch, getState) => {
    const state = getState();
    try {
      const response = await axios(
        await createSignedRequest(
          'POST',
          paymentURL + '/payment/addOn/cancel',
          JSON.stringify({ productId }),
          { Organisation: String(state.currentOrganisation) },
        ),
      );
      const { success, error, min_term_end, remaining_months } = response.data || {};
      if (success) {
        dispatch(toggleAlertMessage(true, response.data.message || 'Add-on cancellation scheduled', 'success'));
        await dispatch(getOrganisation());
        await dispatch(getAddonCatalog());
        return response.data;
      } else if (min_term_end) {
        dispatch(
          toggleAlertMessage(
            true,
            `Minimum term not reached. ${remaining_months} month(s) remaining until ${new Date(min_term_end).toLocaleDateString()}.`,
            'warning',
          ),
        );
        return response.data;
      } else {
        dispatch(toggleAlertMessage(true, error || 'Cancellation failed', 'error'));
        return null;
      }
    } catch (e) {
      console.log('cancelAddOn Error:', e);
      const data = e?.response?.data || {};
      if (data.min_term_end || data.earliest_cancel_date) {
        const date = data.earliest_cancel_date || data.min_term_end;
        dispatch(
          toggleAlertMessage(
            true,
            `Minimum term not reached. ${data.remaining_months || 0} month(s) remaining until ${new Date(date).toLocaleDateString()}.`,
            'warning',
          ),
        );
        return data;
      }
      dispatch(toggleAlertMessage(true, data.error || 'Failed to cancel add-on', 'error'));
      return null;
    }
  };
};

/**
 * Admin: create an add-on product with Stripe Product + Prices (multi-currency).
 * @param {Object} data - `{ name, description, addon_ids, prices: { gbp: { monthly, annual }, ... }, min_term_months }`
 */
const adminCreateAddOnProduct = (data) => {
  return async (dispatch, getState) => {
    const state = getState();
    try {
      const response = await axios(
        await createSignedRequest(
          'POST',
          apiURL + '/admin/portal/addOnProducts/create',
          JSON.stringify(data),
          { Organisation: String(state.currentOrganisation) },
        ),
      );
      const { success, error } = response.data || {};
      if (success || response.data?.id) {
        dispatch(toggleAlertMessage(true, 'Add-on product created with Stripe pricing', 'success'));
        return response.data;
      } else {
        dispatch(toggleAlertMessage(true, error || 'Creation failed', 'error'));
        return null;
      }
    } catch (e) {
      console.log('adminCreateAddOnProduct Error:', e);
      dispatch(toggleAlertMessage(true, 'Failed to create add-on product', 'error'));
      return null;
    }
  };
};

/**
 * Admin: update an add-on product's metadata and/or pricing.
 * @param {string} productId
 * @param {Object} data - fields to update
 */
const adminUpdateAddOnProduct = (productId, data) => {
  return async (dispatch, getState) => {
    const state = getState();
    try {
      const response = await axios(
        await createSignedRequest(
          'PUT',
          apiURL + `/admin/portal/addOnProducts/${productId}`,
          JSON.stringify(data),
          { Organisation: String(state.currentOrganisation) },
        ),
      );
      const { success, error } = response.data || {};
      if (success || response.data?.id) {
        dispatch(toggleAlertMessage(true, 'Add-on product updated', 'success'));
        return response.data;
      } else {
        dispatch(toggleAlertMessage(true, error || 'Update failed', 'error'));
        return null;
      }
    } catch (e) {
      console.log('adminUpdateAddOnProduct Error:', e);
      dispatch(toggleAlertMessage(true, 'Failed to update add-on product', 'error'));
      return null;
    }
  };
};

/**
 * Fetch period-based billing statement JSON for the current organisation.
 * @param {{ from: string, to: string }} params — ISO dates YYYY-MM-DD
 * @returns {Promise<object|null>}
 */
const getStripeStatement = ({ from, to }) => {
  return async (dispatch, getState) => {
    const state = getState();
    const orgId = state.currentOrganisation;

    if (!from || !to) {
      dispatch(toggleAlertMessage(true, 'Please select a statement period.', 'error'));
      return null;
    }

    dispatch(setStatementLoading(true));

    try {
      const response = await axios(
        await createSignedRequest(
          'GET',
          paymentURL + '/payment/stripe/statement' + buildStatementQuery({ from, to }),
          null,
          { Organisation: String(orgId) },
        ),
      );

      const { error, statement } = response.data || {};

      if (error) {
        dispatch(toggleAlertMessage(true, error, 'error'));
        dispatch(setStatement(null));
        return null;
      }

      dispatch(setStatement(statement || response.data));
      return statement || response.data;
    } catch (e) {
      console.log('getStripeStatement Error:', e);
      dispatch(toggleAlertMessage(true, 'There was a problem fetching your account statement.', 'error'));
      dispatch(setStatement(null));
      return null;
    } finally {
      dispatch(setStatementLoading(false));
    }
  };
};

/**
 * Download billing statement PDF for the current organisation.
 * @param {{ from: string, to: string }} params
 * @returns {Promise<boolean>}
 */
const getStripeStatementPdf = ({ from, to }) => {
  return async (dispatch, getState) => {
    const state = getState();
    const orgId = state.currentOrganisation;

    if (!from || !to) {
      dispatch(toggleAlertMessage(true, 'Please select a statement period.', 'error'));
      return false;
    }

    dispatch(setStatementPdfLoading(true));

    try {
      const response = await axios(
        await createSignedRequest(
          'GET',
          paymentURL + '/payment/stripe/statement/pdf' + buildStatementQuery({ from, to }),
          null,
          { Organisation: String(orgId) },
        ),
      );

      const { error } = response.data || {};
      if (error) {
        dispatch(toggleAlertMessage(true, error, 'error'));
        return false;
      }

      const started = openStatementPdfResponse(dispatch, response.data);
      if (!started) {
        dispatch(toggleAlertMessage(true, 'Unable to start statement PDF download.', 'error'));
      }
      return started;
    } catch (e) {
      console.log('getStripeStatementPdf Error:', e);
      dispatch(toggleAlertMessage(true, 'There was a problem generating your statement PDF.', 'error'));
      return false;
    } finally {
      dispatch(setStatementPdfLoading(false));
    }
  };
};

/**
 * Fetch billing statement JSON for any organisation (CYF admin).
 * @param {{ orgId: string, from: string, to: string }} params
 * @returns {Promise<object|null>}
 */
const getAdminOrgStatement = ({ orgId, from, to }) => {
  return async (dispatch) => {
    if (!orgId || !from || !to) {
      dispatch(toggleAlertMessage(true, 'Please select an organisation and statement period.', 'error'));
      return null;
    }

    dispatch(setStatementLoading(true));

    try {
      const response = await axios(
        await createSignedRequest(
          'GET',
          apiURL +
            `/admin/portal/organisations/${encodeURIComponent(orgId)}/billing/statement` +
            buildStatementQuery({ from, to }),
          null,
          {},
        ),
      );

      const { error, statement } = response.data || {};

      if (error) {
        dispatch(toggleAlertMessage(true, error, 'error'));
        dispatch(setStatement(null));
        return null;
      }

      dispatch(setStatement(statement || response.data));
      return statement || response.data;
    } catch (e) {
      console.log('getAdminOrgStatement Error:', e);
      dispatch(toggleAlertMessage(true, 'There was a problem fetching the account statement.', 'error'));
      dispatch(setStatement(null));
      return null;
    } finally {
      dispatch(setStatementLoading(false));
    }
  };
};

/**
 * Download billing statement PDF for any organisation (CYF admin).
 * @param {{ orgId: string, from: string, to: string }} params
 * @returns {Promise<boolean>}
 */
const getAdminOrgStatementPdf = ({ orgId, from, to }) => {
  return async (dispatch) => {
    if (!orgId || !from || !to) {
      dispatch(toggleAlertMessage(true, 'Please select an organisation and statement period.', 'error'));
      return false;
    }

    dispatch(setStatementPdfLoading(true));

    try {
      const response = await axios(
        await createSignedRequest(
          'GET',
          apiURL +
            `/admin/portal/organisations/${encodeURIComponent(orgId)}/billing/statement/pdf` +
            buildStatementQuery({ from, to }),
          null,
          {},
        ),
      );

      const { error } = response.data || {};
      if (error) {
        dispatch(toggleAlertMessage(true, error, 'error'));
        return false;
      }

      const started = openStatementPdfResponse(dispatch, response.data);
      if (!started) {
        dispatch(toggleAlertMessage(true, 'Unable to start statement PDF download.', 'error'));
      }
      return started;
    } catch (e) {
      console.log('getAdminOrgStatementPdf Error:', e);
      dispatch(toggleAlertMessage(true, 'There was a problem generating the statement PDF.', 'error'));
      return false;
    } finally {
      dispatch(setStatementPdfLoading(false));
    }
  };
};

const adminSyncAddOnProductPrices = (productId) => {
  return async (dispatch, getState) => {
    const state = getState();
    try {
      const response = await axios(
        await createSignedRequest(
          'POST',
          apiURL + `/admin/portal/addOnProducts/${productId}/sync-prices`,
          '{}',
          { Organisation: String(state.currentOrganisation) },
        ),
      );
      const { success, error } = response.data || {};
      if (success) {
        dispatch(toggleAlertMessage(true, 'Prices synced from Stripe', 'success'));
        return response.data;
      } else {
        dispatch(toggleAlertMessage(true, error || 'Sync failed', 'error'));
        return null;
      }
    } catch (e) {
      console.log('adminSyncAddOnProductPrices Error:', e);
      dispatch(toggleAlertMessage(true, 'Failed to sync prices', 'error'));
      return null;
    }
  };
};

const Actions = {
  attachPaymentMethodToSubscription,
  selectProduct,
  selectPrice,
  showSubscriptionModal,
  subscriptionPlan,
  registrationBillingDetails,
  getStripeCoupon,
  stripeCoupon,
  createStripeCheckoutSession,
  createStripeCheckoutSessionSetupBacs,
  sendUserOrderForm,
  getStripeInvoices,
  savePricingBand,
  getStripeCustomerId,
  getStripePaymentMethods,
  getStripeIntent,
  updateStripeSubscriptions,
  removePaymentMethod,
  createStripeCustomer,
  setStripeCustomerId,
  getSubscription,
  getSubscriptionPrice,
  updateStripeCustomer,
  getStripePricingTiers,
  getRegistrationPlans,
  setPlanUpgradeSelection,
  contactSales,
  getStripeCustomerIdForDisplay,
  getAddonCatalog,
  purchaseAddOn,
  cancelAddOn,
  adminCreateAddOnProduct,
  adminUpdateAddOnProduct,
  adminSyncAddOnProductPrices,
  getStripeStatement,
  getStripeStatementPdf,
  getAdminOrgStatement,
  getAdminOrgStatementPdf,
};

export { Actions, Types };
